import { useState } from "react";
import { ChevronRight, X, Upload } from "lucide-react";
import { CircleLoader } from "../CircleLoader";
import toast from "react-hot-toast";
import { uploadAccept, validateUploadFile } from "../../utilities/importantFns";

const PRIMARY = "#0F2A6B";

export default function Third({ handleChange, loading, form, nextStep }: any) {
  const [conditionModal, setConditionModal] = useState(false);

  const conditions = ["None", "Water", "Food", "Drug", "Soda"];

  return (
    <div className="min-h-screen bg-blue-50 p-6">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* HEADER */}
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-800">
            Medical & Profile
          </h1>

          <button
            onClick={() => nextStep(1)}
            className="text-sm text-blue-600 hover:underline"
          >
            ← Back
          </button>
        </div>

        {/* PROGRESS (STEP 2 = 66%) */}
        <div className="h-1.5 bg-gray-200 rounded-full">
          <div
            className="h-full rounded-full"
            style={{ width: "66%", backgroundColor: PRIMARY }}
          />
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            nextStep(3); // move to actual step 3
          }}
          className="space-y-6"
        >
          {/* MEDICAL */}
          <Card title="Medical Condition">
            <div
              onClick={() => setConditionModal(true)}
              className="px-4 py-3 rounded-xl border bg-white cursor-pointer flex justify-between items-center transition hover:border-blue-400 hover:shadow-sm"
            >
              <span className={!form.condition?.[0] ? "text-gray-400" : ""}>
                {form.condition?.[0] || "Select Condition"}
              </span>
              <ChevronRight size={18} />
            </div>

            {form.condition?.[0] && form.condition[0] !== "None" && (
              <div className="mt-4">
                <p className="text-xs text-gray-500 mb-2">Severity *</p>
                <div className="flex gap-2">
                  {["Mild", "Moderate", "Severe"].map((level) => (
                    <div
                      key={level}
                      onClick={() =>
                        handleChange({
                          name: "extent",
                          value: [level],
                        })
                      }
                      className="flex-1 text-center py-2 rounded-lg cursor-pointer border transition-all"
                      style={{
                        backgroundColor:
                          form.extent?.[0] === level ? PRIMARY : "",
                        color: form.extent?.[0] === level ? "white" : "",
                      }}
                    >
                      {level}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>

          {/* IMAGE */}
          <Card title="Profile Picture">
            <div className="flex flex-col items-center gap-4">
              <div className="relative w-32 h-32 rounded-xl border-2 border-dashed flex items-center justify-center overflow-hidden bg-gray-50 hover:border-blue-400 transition">
                <input
                  type="file"
                  name="profile_picture"
                  accept={uploadAccept.image}
                  className="absolute inset-0 opacity-0 cursor-pointer"
                  onChange={({ target }: any) => {
                    const file = target.files?.[0];

                    if (!file) return;

                    if (file.size > 10000000) {
                      toast.error("File exceeds 9MB");
                      return;
                    }

                    const validation = validateUploadFile(file, "image");
                    if (!validation.valid) {
                      toast.error(validation.message);
                      target.value = "";
                      return;
                    }

                    handleChange(target);
                  }}
                  required={!form.profile_picture}
                />

                {form.profile_picture ? (
                  <img
                    src={URL.createObjectURL(form.profile_picture)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <Upload className="text-gray-400" size={30} />
                )}
              </div>

              <p className="text-xs text-gray-500">Max file size: 9MB</p>
            </div>
          </Card>

          {/* BUTTON */}
          <div className="flex justify-end">
            <button
              type="submit"
              className="px-6 py-2.5 rounded-lg text-white font-medium flex items-center gap-2 transition-all hover:shadow-lg hover:scale-[1.03] active:scale-[0.97]"
              style={{ backgroundColor: PRIMARY }}
            >
              {loading ? <CircleLoader /> : "Continue"}
              {!loading && <ChevronRight size={16} />}
            </button>
          </div>
        </form>
      </div>

      {/* MODAL */}
      {conditionModal && (
        <Modal
          title="Select Condition"
          onClose={() => setConditionModal(false)}
        >
          {conditions.map((c) => (
            <Item
              key={c}
              label={c}
              onClick={() => {
                handleChange({
                  name: "condition",
                  value: [c],
                });
                setConditionModal(false);
              }}
            />
          ))}
        </Modal>
      )}
    </div>
  );
}

/* ================= COMPONENTS ================= */

const Card = ({ title, children }: any) => (
  <div className="group bg-white rounded-2xl p-5 border shadow-sm transition-all duration-300 hover:shadow-[0_20px_50px_rgba(15,42,107,0.12)] hover:-translate-y-1 hover:border-blue-200">
    <h2 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
      <span className="w-1.5 h-4 bg-blue-600 rounded-full opacity-70 group-hover:opacity-100 transition"></span>
      {title}
    </h2>
    {children}
  </div>
);

const Modal = ({ children, onClose, title }: any) => (
  <div className="fixed inset-0 bg-black/30 backdrop-blur-[2px] flex items-center justify-center z-50">
    <div className="bg-white w-full max-w-md rounded-2xl shadow-[0_30px_80px_rgba(0,0,0,0.2)]">
      <div className="flex justify-between items-center px-4 py-3 border-b">
        <h3 className="font-medium">{title}</h3>
        <X className="cursor-pointer" onClick={onClose} />
      </div>
      <div className="p-2 max-h-80 overflow-y-auto">{children}</div>
    </div>
  </div>
);

const Item = ({ label, onClick }: any) => (
  <div
    onClick={onClick}
    className="px-4 py-3 cursor-pointer rounded-lg transition-all hover:bg-blue-50 hover:translate-x-1 active:scale-[0.98]"
  >
    {label}
  </div>
);
